<template>
  <div class="member-bets">
    <div class="d-flex flex-wrap mb-3" style="gap: 20px;">
      <div class="d-flex flex-column">
        <span class="font-weight-bold">Date</span>
        <div class="d-flex align-items-center" style="gap: 5px;">
          <a-date-picker
            v-model="filters.date_start"
            format="YYYY-MM-DD HH:mm"
            :show-time="{
              defaultValue: $moment('00:00:00', 'HH:mm'),
              format: 'HH:mm'
            }"
            type="date"
            size="large"
            placeholder="Start date"
            :disabled="loading || !hasListData"
            @change="() => {resetPage(); getUserBetsHistoryData()}"
          />
          <span>-</span>
          <a-date-picker
            v-model="filters.date_end"
            format="YYYY-MM-DD HH:mm"
            :show-time="{
              defaultValue: $moment('00:00:00', 'HH:mm'),
              format: 'HH:mm'
            }"
            type="date"
            size="large"
            placeholder="End date"
            :disabled="loading || !hasListData"
            @change="() => {resetPage(); getUserBetsHistoryData()}"
          />
        </div>
      </div>
      <div class="d-flex align-items-center" style="gap: 5px;">
        <div class="d-flex flex-column">
          <span class="font-weight-bold">Min bet</span>
          <a-input-number
            v-model="filters.bet_min"
            type="number"
            size="large"
            placeholder="Min bet"
            style="min-width: 120px;"
            :disabled="!hasListData"
            :loading="loading"
            @change="changeBetFilters('betMinTimeout')"
          />
        </div>
        <span style="height: 40px;" class="d-flex align-self-end align-items-center">-</span>
        <div class="d-flex flex-column">
          <span class="font-weight-bold">Max bet</span>
          <a-input-number
            v-model="filters.bet_max"
            type="number"
            size="large"
            placeholder="Max bet"
            style="min-width: 120px;"
            :disabled="!hasListData"
            :loading="loading"
            @change="changeBetFilters('betMaxTimeout')"
          />
        </div>
      </div>
      <UniversalSelect
        v-model="filters.free_bet"
        style="width: 150px;"
        :options="isFreeBetOptions"
        placeholder="Is Free bet"
        title="Is Free bet"
        :disabled="loading || !hasListData"
        @change="() => {resetPage(); getUserBetsHistoryData()}"
      />
      <UniversalSelect
        v-model="filters.result"
        style="width: 150px;"
        :options="resultOptions"
        placeholder="Result"
        title="Result"
        :disabled="loading || !hasListData"
        @change="() => {resetPage(); getUserBetsHistoryData()}"
      />
      <div class="d-flex align-items-end" style="height: 62px;">
        <SearchField
          :value="filters.search"
          :loading="loading"
          :disabled="loading || !hasListData"
          @search="changeSearchValue"
        />
      </div>
      <div class="d-flex align-items-end justify-content-end" style="flex: 1 1 auto;">
        <a-button
          v-if="isFiltered"
          size="large"
          :disabled="loading"
          class="w-fit"
          @click="handleReset"
        >
          Reset filters
        </a-button>
        <a-tooltip
          placement="topLeft"
          arrow-point-at-center
          :get-popup-container="getPopupContainer"
          :overlayStyle="{'min-width': 'fit-content'}"
        >
          <template v-if="!data.length" slot="title">
            <span>The table is empty</span>
          </template>
          <a-button
            type="primary"
            size="large"
            :disabled="loading || !data.length"
            class="w-fit ml-2"
            @click="toggleExportModal(true)"
          >
            <a-icon type="upload" /> Export
          </a-button>
        </a-tooltip>
      </div>
    </div>
    <a-table
      :dataSource="data"
      :pagination="meta"
      :columns="columns"
      :row-key="(record) => record.id"
      :loading="loading"
      @change="changePagination"
    >
      <template slot="date" slot-scope="date">
        <span>{{ date ? formatDate(date) : '-' }}</span>
      </template>
      <template slot="matches" slot-scope="matches, record">
        <span v-if="!matches && matches?.length">-</span>
        <a-tooltip
          v-else
          placement="topLeft"
          arrow-point-at-center
          :get-popup-container="getPopupContainer"
          :overlayStyle="{'min-width': 'fit-content'}"
        >
          <template slot="title">
            <div class="d-flex flex-column">
              <div v-for="(match, index) in matches" :key="index">
                {{ match.champ }} ({{ match.sport }}): {{ match.name }}<br><br v-if="matches.length !== index + 1">
              </div>
            </div>
          </template>
          <!-- <template v-if="record.bet_type !== 'express'" slot="title">
            <div v-for="(match, index) in matches" :key="index">
              {{ match.champ }} ({{ match.sport }}): {{ match.name }}<br><br v-if="matches.length !== index + 1">
            </div>
          </template> -->
          <span v-html="getMatchesName(matches, record)" />
        </a-tooltip>
      </template>
      <template slot="bet_sum" slot-scope="bet_sum, record">
        <span>{{ bet_sum ? `${bet_sum} ${record?.currency}` : '-' }}</span>
      </template>
      <template slot="is_free_bet" slot-scope="is_free_bet">
        <span>{{ is_free_bet ? 'Yes' : 'No' }}</span>
      </template>
      <template slot="pay_sum" slot-scope="pay_sum, record">
        <span>{{ pay_sum ? `${pay_sum} ${record?.currency}` : '-' }}</span>
      </template>
      <template slot="result" slot-scope="result">
        <a-badge
          :count="result === 'win' ? 'Win' : 'Loss'"
          :number-style="{
            backgroundColor: resultStatusColor[result].background,
            color: resultStatusColor[result].color,
          }"
          style="transform: scale(1.2);"
        />
      </template>
    </a-table>
    <TotalCount :count="meta.total" />
    <ExportModal
      v-if="showExportModal"
      :show="showExportModal"
      title="Export bets history"
      text="A <b>.xlsx file</b> with bets history, based on your table filters, will be sent to the specified <b>email address</b>."
      :loading="exportLoading"
      @confirm="exportLogs"
      @close="toggleExportModal(false)"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import UniversalSelect from '@/components/custom/filters/universalSelect.vue'
import SearchField from '@/components/custom/form/SearchField.vue'
import ExportModal from '@/views/apps/referral-program/referral-programs/components/ExportModal.vue'
import TotalCount from '@/views/apps/referral-program/components/TotalCount.vue'
import { getTableMeta } from '@/composables/useGetTableMeta.js'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'
import { setQueryParams, setFilters } from '@/composables/useSetQueryParams.js'
import { getFilledFilters } from '@/composables/useGetFilledFilters.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  components: {
    UniversalSelect,
    SearchField,
    ExportModal,
    TotalCount,
  },
  data: () => ({
    data: [],
    meta: {
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '40'],
    },
    filters: {
      page: undefined,
      per_page: undefined,
      date_start: undefined,
      date_end: undefined,
      bet_min: undefined,
      bet_max: undefined,
      free_bet: undefined,
      result: undefined,
      search: undefined,
      sort_field: undefined,
      sort_dir: undefined,
    },
    columns: [
      {
        title: 'Date',
        dataIndex: 'date',
        width: '15%',
        ellipsis: true,
        scopedSlots: { customRender: 'date' },
        sorter: true,
      },
      {
        title: 'Match name',
        dataIndex: 'matches',
        width: '20%',
        scopedSlots: { customRender: 'matches' },
        ellipsis: true,
      },
      {
        title: 'Bet amount',
        dataIndex: 'bet_sum',
        width: '10%',
        scopedSlots: { customRender: 'bet_sum' },
      },
      {
        title: 'Free bet',
        dataIndex: 'is_free_bet',
        width: '10%',
        scopedSlots: { customRender: 'is_free_bet' },
      },
      {
        title: 'Payout Amount',
        dataIndex: 'pay_sum',
        width: '10%',
        scopedSlots: { customRender: 'pay_sum' },
      },
      {
        title: 'Result',
        dataIndex: 'result',
        width: '10%',
        scopedSlots: { customRender: 'result' },
      },
    ],
    isFreeBetOptions: [
      { name: 'Yes', value: 1 },
      { name: 'No', value: 0 },
    ],
    resultOptions: [
      { name: 'Win', value: 'win' },
      { name: 'Loss', value: 'lose' },
    ],
    resultStatusColor: {
      win: {
        color: '#fff',
        background: '#28a745',
      },
      lose: {
        color: '#fff',
        background: '#dc3545',
      },
    },
    betMinTimeout: null,
    betMaxTimeout: null,
    showExportModal: false,
    exportLoading: false,
    loading: false,
  }),
  computed: {
    programId() {
      return this.$route.params.id
    },
    roleSystemName() {
      return this.$route.params.role
    },
    isHasListItems() {
      return !!this.data?.length
    },
    isFiltered() {
      return Object.values(this.filters).some(v => v !== undefined && v !== null && v !== '')
    },
    hasListData() {
      return this.isHasListItems || this.isFiltered
    },
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    formatDate(date) {
      return this.$moment(date).tz(this.$moment.tz.guess()).format('DD.MM.YYYY HH:mm')
    },
    getMatchesName(matches, item) {
      if (item.bet_type === 'ordinar') {
        return `<b>Ординар</b>: ${matches[0].name}<br>${matches[0].champ} (${matches[0].sport})`
      }
      return `<b>Экспресс</b>: (${matches?.length})`
    },
    toggleExportModal(status = false) {
      this.showExportModal = status
    },
    changeBetFilters(target) {
      clearTimeout(this[target])

      this[target] = setTimeout(() => {
        this.resetPage()
        this.getUserBetsHistoryData()
        clearTimeout(this[target])
      }, 1000)
    },
    changeSearchValue(value) {
      this.filters.search = value
      this.resetPage()
      this.getUserBetsHistoryData()
    },
    resetPage() {
      this.filters.page = 1
    },
    changePagination(pagination, _, sorter) {
      this.filters.page = pagination.current
      this.filters.per_page = pagination.pageSize
      if (sorter && sorter?.order) {
        this.filters.sort_field = sorter.field
        this.filters.sort_dir = this.getSortDirectionParam(sorter.order)
      } else {
        this.filters.sort_field = undefined
        this.filters.sort_dir = undefined
      }
      this.getUserBetsHistoryData()
    },
    getSortDirectionParam(order) {
      if (order === 'ascend') {
        return 'asc'
      }
      return 'desc'
    },
    getFilters() {
      setQueryParams(this.filters, this, 'bet', true)
      if (!this.isFiltered) {
        return {}
      }

      return getFilledFilters({ filters: this.filters, dateFields: ['date_start', 'date_end'] })
    },
    async getUserBetsHistoryData() {
      try {
        this.loading = true

        const memberId = this.$route.params.member_id
        const filters = this.getFilters()

        const url = `${referralApiUrl}/referral-programs/${this.programId}/members/${this.roleSystemName}/${memberId}/bet-history`
        const response = await apiClient.get(url, { params: filters })

        if (response?.data?.data) {
          this.data = response.data.data
          const newMeta = getTableMeta(response?.data?.meta)
          Object.assign(this.meta, newMeta)
          return
        }
        getErrorMessage(response?.data)
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    async exportLogs(email) {
      try {
        this.exportLoading = true

        const memberId = this.$route.params.member_id
        const filters = this.getFilters()
        delete filters.page
        delete filters.per_page
        filters.email = email

        const url = `${referralApiUrl}/referral-programs/${this.programId}/members/${this.roleSystemName}/${memberId}/bet-history/export`
        const response = await apiClient.get(url, { params: filters })

        if (response?.data?.data) {
          const message = response.data.data?.message || 'We`ve started exporting your program`s activity logs, and you`ll receive them by email shortly.'
          this.$notification.success({
            message,
          })
          this.toggleExportModal()
          return
        }
        getErrorMessage(response?.data)
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.exportLoading = false
      }
    },
    handleReset() {
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = undefined
      })
      this.getUserBetsHistoryData()
    },
  },
  async mounted() {
    if (Object.keys(this.$route.query)?.length) {
      setFilters(this.$route.query, this, 'bet', true)
    }
    this.getUserBetsHistoryData()
  },
}
</script>

<style lang="scss">
.member-bets {
  .ant-table-row {
    cursor: default !important;
  }
}
</style>
